import React from 'react';

import {
  DEFAULT_THEME,
  isThemeSupported,
  resolveTheme,
  THEME_ALIASES,
  ThemeContext,
} from '@neptune/shared/venus-domain';

import { WithNonInterferingComponent } from '../../modules/utils';

type ThemeProviderOwnProps = {
  theme?: string;
};

type ThemeProviderProps<P extends object> = WithNonInterferingComponent<P, ThemeProviderOwnProps> &
  React.HTMLAttributes<HTMLSpanElement> &
  ThemeProviderOwnProps & {
    children?: React.ReactNode | React.ReactNode[];
  };

function BaseThemeProvider<P extends object>({
  component: Component = 'span',
  theme,
  element,
  ...passProps
}: ThemeProviderProps<P> & { element?: HTMLElement }) {
  const themeAlias = isThemeSupported(theme) ? theme : DEFAULT_THEME;
  const currentTheme = resolveTheme(themeAlias);

  const themeContext = React.useMemo(() => {
    return { themeAlias, theme: currentTheme };
  }, [themeAlias, currentTheme]);

  React.useEffect(() => {
    if (!element) {
      return;
    }

    if (theme == null || theme === THEME_ALIASES.BROWSER) {
      element.removeAttribute('data-theme');
    } else {
      element.setAttribute('data-theme', theme);
    }

    return () => {
      element.removeAttribute('data-theme');
    };
  }, [element, theme]);

  return (
    <ThemeContext.Provider value={themeContext}>
      {Component === React.Fragment ? (
        /* making React happy about invalid data-theme prop on `React.Fragment` */
        <Component {...passProps} />
      ) : (
        <Component data-theme={currentTheme} {...passProps} />
      )}
    </ThemeContext.Provider>
  );
}

export function ThemeProvider<P extends object>({ ...passProps }: ThemeProviderProps<P>) {
  return <BaseThemeProvider {...passProps} />;
}

export function RootThemeProvider({
  theme,
  children,
}: ThemeProviderOwnProps & { children?: React.ReactNode }) {
  return (
    <BaseThemeProvider component={React.Fragment} theme={theme} element={document.documentElement}>
      {children}
    </BaseThemeProvider>
  );
}
